/**
 * Mobile-wizard-specific literals.
 *
 * The raw colour / gradient / timing values that ONLY the dedicated mobile
 * wizard (`mobile/**`) uses, ported from the standalone app's `src/mobile/**`
 * components. They re-use the shared `CFG` palette / `CFG_GRADIENTS` where
 * possible so this file holds only what is genuinely mobile-local, keeping the
 * mobile styled-components free of raw literals.
 *
 * DO NOT add page-engine colours here — the desktop steps own their own
 * `*.tokens.ts`; this is exclusively the phone-width experience.
 */
import { themed } from '../../../theme/themeRegistry';
import { CFG } from '../../../theme/configurator-tokens';

// Shared mobile chrome --------------------------------------------------------
export const MOBILE = themed('cfgMobile', {
  /** Near-black page background used across all mobile screens. */
  screenBg: '#0a0a0b',
  /** Full-screen site preview background. */
  previewBg: '#0a0a0d',
  /** Bottom-sheet / panel surface. */
  sheetBg: '#18181b',
  panelBg: '#151518',
  /** Pill / chip surface in the scan + detail screens. */
  pillBg: '#141417',
  /** Track behind the market gauge. */
  gaugeTrack: '#080809',
  /** Header title gradient (left → right). */
  titleGradient: 'linear-gradient(90deg, #aea6f1 0%, #cec1cc 50%, #da9c74 100%)',
  /** Scan status title gradient (pink → cyan). */
  scanGradient: 'linear-gradient(22.4deg, #ff40e0 5.215%, #40e8ff 94.785%)',
  /** Feature-detail header gradient. */
  detailGradient: 'linear-gradient(90deg, #afa7ee 0%, #c3b8d6 100%)',

  // Progress segments
  segActive: CFG.brand,
  segDone: '#ffffff',
  segIdle: 'rgba(255, 255, 255, 0.2)',

  // Generic washes / scrims
  hairline: 'rgba(255, 255, 255, 0.1)',
  hairlineSoft: 'rgba(255, 255, 255, 0.05)',
  /** Faint white text (terms link, struck price). */
  whiteFaint: 'rgba(255, 255, 255, 0.5)',
  whiteDim: 'rgba(255, 255, 255, 0.6)',
  /** Header scrim band (black → transparent). */
  headerScrim: 'linear-gradient(to bottom, #000 20.192%, rgba(0, 0, 0, 0) 100%)',
  /** Header fade — the mirror of `bottomFade` (screen bg opaque under the title → transparent). */
  headerFade: 'linear-gradient(to bottom, #0a0a0b 55%, rgba(10, 10, 11, 0) 100%)',
  /** Bottom fade behind a step CTA. */
  bottomFade: 'linear-gradient(to top, #0a0a0b 55%, rgba(10, 10, 11, 0) 100%)',
  /** Heavier bottom fade (finish step). */
  bottomFadeHeavy: 'linear-gradient(to top, #0a0a0b 75%, rgba(10, 10, 11, 0) 100%)',
  /** Design-step scrim between phone + sheet. */
  designScrim:
    'linear-gradient(to top, rgba(0,0,0,0.94) 0%, rgba(0,0,0,0.94) 30%, rgba(0,0,0,0) 100%)',
  /** Carousel right-edge fade mask. */
  carouselFade: 'linear-gradient(to right, black 85%, transparent 98.3%)',
  /** Custom-config horizontal mask. */
  hMask: 'linear-gradient(90deg, transparent 0%, black 20%, black 80%, transparent 100%)',
  /** Custom-config vertical video mask. */
  vMask: 'linear-gradient(180deg, transparent 0%, black 20%, black 80%, transparent 100%)',

  // Loader mark fill.
  white: '#ffffff',
  /** Pure black used by knockout glyphs (close X, etc). */
  black: '#000000',

  // Selection ring
  selectRing: CFG.brand,
  /** Selected design-card glow. */
  selectGlow: '0 27px 64px 0 rgba(255, 121, 27, 0.2)',

  // Tabs
  tabIdle: 'rgba(250, 250, 250, 0.5)',

  // Cards / surfaces
  designCardBg: '#323f48',
  styleCardBg: '#0d0d0f',
  optionCardBg: '#111015',
  chipBg: '#27272a',
  blackSurface: '#000000',

  // Player option overlay (text-side fade).
  optionOverlay:
    'linear-gradient(275.67deg, rgb(17,16,21) 54.7%, rgba(26,26,26,0) 97.4%)',
  // "Recommended" badge fill (black scrim + lighter white wash) — mirrors the
  // desktop players-step pill so the badge reads over the option photo.
  recommendedFill:
    'linear-gradient(90deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.72) 100%),' +
    'linear-gradient(90deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.08) 100%)',
  // Custom-config tier pill background.
  tierPillBg:
    'radial-gradient(48px 32px at 50% 250%, rgba(160,160,160,0.7), rgba(160,160,160,0.06)), #000',
  tierPillTopBorder: 'rgba(255, 121, 27, 0.5)',
  // Pack-detail feature-tile pill.
  featurePillBg:
    'radial-gradient(48px 32px at 50% 250%, rgba(160,160,160,0.7), rgba(160,160,160,0.06)), #000',

  // Misc text colours
  scanStatusText: CFG.base50,
  competitorAccent: '#2a0606',
  knockout: '#141416',
  /** Muted tier label (not included). */
  tierMuted: '#8a8a8c',
  /** Included green check. */
  scanCheck: CFG.success,

  // Sub-pixel borders
  chipBorder: 'rgba(255, 255, 255, 0.07)',
  youBorder: 'rgba(255, 255, 255, 0.2)',

  // Pack-detail
  detailBackdrop: '#080809',
  /** Feature tile dark surface. */
  tileBg: 'rgba(0, 0, 0, 0.7)',
  /** Soft drop shadow used on chips / pills. */
  softShadow: '0 4px 8px rgba(0, 0, 0, 0.25)',
  clusterShadow: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.25))',
  youShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',

  // Custom-config panel
  chevronBg: 'rgba(0, 0, 0, 0.2)',
  panelShadow: '0 -4px 12px rgba(0, 0, 0, 0.25)',
  selectorShadow: 'drop-shadow(0 -4px 8px rgba(0, 0, 0, 0.25))',

  // Payment sheet scrim.
  paymentScrim: 'linear-gradient(90deg, rgba(0,0,0,0.48) 0%, rgba(3,3,6,0.82) 100%)',
} as const);

// Fixed inks for text / glyphs / logo backplates that ride the mobile wizard's
// KEPT-DARK art (the gem pack cards, the pack-detail panel, the scan fan cards,
// and the close/expand/toggle discs). Those surfaces stay dark in BOTH themes, so
// `MOBILE.white` (which flips to dark ink on light) would render dark-on-dark —
// use these constant whites instead. NOT theme-wrapped (they must never flip).
export const MOBILE_INK = {
  /** Primary text/glyph on kept-dark art (gem-card name, panel heading, the X / + glyph). */
  onDark: '#ffffff',
  /** Muted text on kept-dark art (gem-card description, fan-card sub). */
  onDarkDim: 'rgba(255, 255, 255, 0.7)',
  /** Brand-logo disc backplate (competitor logo circles) — stays white in both themes. */
  logoDisc: '#ffffff',
} as const;

// Mobile-only timings (the standalone JS animation loops) ---------------------
export const MOBILE_ANIM = {
  /** Design intro staged-entrance beats (ms). */
  introPhoneIn: 60,
  introCta: 900,
  introSheet: 1500,
  introDone: 2100,
  /** Sheet height (px). */
  sheetHeight: 225,
  /** Preview overlay fade. */
  previewFadeMs: 300,
  /** Pack-detail / feature-detail slide. */
  slideMs: 500,
  /** Design intro: hold the scrim's fade-in this long after the sheet starts rising,
   *  so it appears once the sheet is partway up rather than from the first frame. */
  scrimDelayMs: 300,
  detailSlideMs: 400,
  /** Packs list: fan → grid arrange transition (card slot transform/opacity). */
  packsArrangeMs: 700,
  /** Packs list: delay AFTER the cards settle in the grid before "Custom" fades in. */
  packsCustomDelayMs: 300,
  /** Pack-scan dot interval. */
  scanDotMs: 420,
  /** Pill tick delay (loader → green check). */
  pillTickMs: 850,
  /** Highlight description typing (mirrors the desktop reveal): pre-delay + per-char. */
  typePreMs: 805,
  typeCharMs: 17,
} as const;

// MobilePackAnim CSS animation/transition timings ----------------------------
// Each value is the full timing part of an `animation:` (duration + easing +
// optional delay + fill-mode) or `transition:` (duration + easing + optional
// delay). Keyframe NAMES stay in the component (imported from
// configurator-keyframes); only the timing belongs here. Values are 1:1 with the
// prior inline literals.
export const PACK_ANIM = {
  /** SpinnerWrap enter/exit (opacity + transform). */
  spinnerInOut: '500ms ease-out',
  /** PillStack show/hide fade. */
  stackFade: '500ms ease',
  /** PillSlot descent — the 120ms delay holds older pills back until the new one lands. */
  slotShift: '450ms ease-out 120ms',
  /** Pill grow-in. */
  pillGrow: '0.7s cubic-bezier(0.16, 1, 0.3, 1) both',
  /** Border-beam fade-out. */
  beamFade: '1.5s ease 0.1s both',
  /** Border-beam one-revolution spin. */
  beamSpin: '1.3s cubic-bezier(0.4, 0, 0.4, 1) 0.1s both',
  /** Pill name reveal. */
  pillName: '0.45s ease 0.25s both',
  /** Pop-in (tick, chips, "+N more") — overshoot easing. */
  pop: '300ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
  /** Status loader spin. */
  loaderSpin: '800ms linear infinite',
  /** Chip horizontal slide. */
  chipSlide: '300ms ease-out',
  /** Fan card settle (transform + opacity). */
  fanCard: '700ms ease-out',
  /** Mini loader spin. */
  miniSpin: '2.4s linear infinite',
} as const;

// Pack-scan ring geometry -----------------------------------------------------
export const RING = themed('cfgMobileRing', {
  r: 53.6,
  strokeWidth: 6.84,
  /** Ring base track opacity. */
  trackOpacity: 0.15,
  arcShadow: 'drop-shadow(0 5px 8.9px rgba(43, 142, 195, 0.9))',
  gradFrom: '#ff00d4',
  gradTo: '#00e0ff',
  miniBorder: 'rgba(0, 224, 255, 0.25)',
  miniDotGlow: '0 0 6px 2px rgba(197, 145, 246, 0.9)',
} as const);

export const RING_C = 2 * Math.PI * RING.r;

/** Status spinner stroke + green check. */
export const SCAN = themed('cfgMobileScan', {
  loaderStroke: 'rgba(255, 255, 255, 0.55)',
  check: '#17c964',
} as const);

/** Competitor-pill border-beam (sweeping pink→cyan ring on entrance). */
export const PILL_BEAM = themed('cfgMobilePillBeam', {
  /** Rotating conic gradient that draws the 1.5px ring. */
  gradient:
    'conic-gradient(transparent 0deg, rgba(255, 0, 212, 0.9) 70deg, rgba(0, 224, 255, 0.95) 130deg, transparent 200deg)',
  /** Solid inner cover that leaves only a 1.5px ring of the gradient visible. */
  cover: MOBILE.pillBg,
} as const);
